import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, X } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useIsAuthenticated } from '@/core/auth/SessionStore'
import { PickerSheet } from '@/features/common/PickerSheet'
import { useServicosDoProjeto } from '@/core/db/daos'
import { refreshAllCatalogs } from '@/core/sync/catalogSync'
import { cn } from '@/lib/cn'
import { useProducaoStore, type ApontamentoDraft } from '@/features/producao/producaoStore'
import { ServicoFormSheet } from '@/features/producao/ServicoFormSheet'
import { saveProducaoSession } from '@/features/producao/producaoRepo'
import type { CatServico } from '@/core/db/entities'

/**
 * Tela principal da sessão de produção (replica `SessionScreen.kt`).
 *
 * - FAB "Adicionar serviço" → picker filtrado por projeto via `cdt_projeto_tem_servico_executado`
 * - Cada apontamento abre o `ServicoFormSheet` (flag-driven)
 * - "Finalizar sessão" persiste tudo em out_producao e tenta push (foreground)
 * - Sem itens → tela vazia com instrução
 */
export function ProducaoSessionRoute() {
  const authed = useIsAuthenticated()
  const navigate = useNavigate()

  const store = useProducaoStore()
  const servicos = useServicosDoProjeto(store.projetoId, { apenasCarreteiro: false })

  const [showServicoPicker, setShowServicoPicker] = useState(false)
  const [editingServico, setEditingServico] = useState<CatServico | null>(null)
  const [editingDraft, setEditingDraft] = useState<ApontamentoDraft | null>(null)
  const [finalizing, setFinalizing] = useState(false)
  const [finalizeError, setFinalizeError] = useState<string | null>(null)

  if (!authed) return <Navigate to="/login" replace />
  // Se o usuário entrou direto em /producao/session sem store, manda pra LISTA
  // (que é o ponto canônico). De lá ele pode criar nova OU continuar uma existente.
  if (store.projetoId == null) return <Navigate to="/producao" replace />

  async function handleFinalize() {
    setFinalizing(true)
    setFinalizeError(null)
    try {
      await saveProducaoSession(store)
      store.reset()
      // Volta pra lista; o usuário vê a sessão dele já listada (sincronizada ou pendente).
      navigate('/producao', { replace: true })
    } catch (e) {
      setFinalizeError(e instanceof Error ? e.message : 'Erro ao finalizar')
    } finally {
      setFinalizing(false)
    }
  }

  const totalServicos = servicos?.length ?? 0

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      {/* Top bar — Voltar vai pra LISTA, não pro Setup. Setup é só pra criar nova. */}
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/producao')}
            aria-label="Voltar"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-on-surface truncate">Sessão de produção</h1>
            <p className="text-xs text-on-surface-dim truncate">
              {[
                store.projetoCodigo,
                store.encarregadoNome,
                store.equipeNome,
                formatBR(store.dt),
              ]
                .filter(Boolean)
                .join(' · ')}{' '}
              · {store.itens.length} apontamento(s)
            </p>
          </div>
        </div>
      </header>

      {/* Lista de itens (ou estado vazio) */}
      <div className="flex-1 overflow-y-auto">
        {store.itens.length === 0 ? (
          <div className="flex h-full items-center justify-center p-8">
            <div className="text-center">
              <p className="text-on-surface font-medium">Nenhum serviço apontado ainda</p>
              <p className="text-sm text-on-surface-dim mt-2">
                Toque em "Adicionar serviço" para começar.
              </p>
            </div>
          </div>
        ) : (
          <ul className="p-4 space-y-3 max-w-2xl mx-auto">
            {store.itens.map((item) => (
              <li key={item.id}>
                <ApontamentoCard
                  item={item}
                  onClick={() => {
                    setEditingServico(item.servico)
                    setEditingDraft(item)
                  }}
                  onDelete={() => store.removeItem(item.id)}
                />
              </li>
            ))}
            {/* Espaço pro FAB não tampar o último card */}
            <li className="h-20" />
          </ul>
        )}
      </div>

      {/* FAB */}
      <button
        type="button"
        onClick={() => setShowServicoPicker(true)}
        className={cn(
          'fixed bottom-24 right-4 z-20 sm:right-1/2 sm:translate-x-1/2 sm:max-w-md sm:w-full sm:pr-4',
          'flex justify-end',
        )}
      >
        <span
          className={cn(
            'inline-flex items-center gap-2 rounded-full bg-accent text-bg-base',
            'px-5 py-3 font-semibold shadow-lg',
            'hover:opacity-90 active:opacity-80 transition-opacity',
          )}
        >
          <Plus className="h-5 w-5" />
          Adicionar serviço
        </span>
      </button>

      {/* Bottom action */}
      {store.itens.length > 0 && (
        <div className="sticky bottom-0 bg-bg-base/95 backdrop-blur border-t border-border p-4 pb-safe-plus-4 pl-safe pr-safe space-y-2">
          {finalizeError && (
            <p className="text-sm text-bad text-center">{finalizeError}</p>
          )}
          <Button
            variant="primary"
            size="lg"
            disabled={finalizing}
            onClick={handleFinalize}
            className="w-full"
          >
            {finalizing ? 'Salvando…' : `Finalizar sessão (${store.itens.length})`}
          </Button>
        </div>
      )}

      {showServicoPicker && (
        <PickerSheet
          open
          title="Selecionar serviço"
          items={servicos ?? []}
          label={(s) => s.nome}
          sublabel={(s) =>
            `${s.tp} · ${s.unidadeNome ?? '—'} · ${flagsTags(s)}`
          }
          onPick={(s) => {
            setShowServicoPicker(false)
            setEditingServico(s)
            setEditingDraft(null)
          }}
          onDismiss={() => setShowServicoPicker(false)}
          searchHint="Buscar serviço"
          emptyMessage={
            totalServicos === 0
              ? `Sem serviços visíveis para o projeto ${store.projetoCodigo}. Verifique cdt_projeto_tem_servico_executado.`
              : 'Nenhum serviço encontrado'
          }
          emptyAction={
            totalServicos === 0
              ? { label: 'Sincronizar agora', onClick: () => void refreshAllCatalogs() }
              : undefined
          }
        />
      )}

      {editingServico && (
        <ServicoFormSheet
          servico={editingServico}
          projetoId={store.projetoId}
          initial={editingDraft}
          onSave={(draft) => {
            store.addOrUpdateItem(draft)
            setEditingServico(null)
            setEditingDraft(null)
          }}
          onDismiss={() => {
            setEditingServico(null)
            setEditingDraft(null)
          }}
        />
      )}
    </main>
  )
}

// ----------------------------------------------------------------------------
//  Card de apontamento
// ----------------------------------------------------------------------------

function ApontamentoCard({
  item,
  onClick,
  onDelete,
}: {
  item: ApontamentoDraft
  onClick: () => void
  onDelete: () => void
}) {
  const tags = [
    item.materialNome,
    item.materialRedeNome,
    item.diametroNome,
    item.tpRedeNome,
    item.trechoNome,
    item.estaca ? `est. ${item.estaca}` : null,
  ].filter((x): x is string => x != null)

  return (
    <div className="card-surface flex items-stretch overflow-hidden">
      <button type="button" onClick={onClick} className="flex-1 p-4 text-left min-w-0">
        <p className="text-on-surface font-medium truncate">{item.servico.nome}</p>
        <div className="mt-1 flex items-baseline gap-3 flex-wrap">
          {item.qtd != null && (
            <span className="text-accent text-lg font-bold">
              {formatNumber(item.qtd)} {item.servico.unidadeNome ?? ''}
            </span>
          )}
          {tags.length > 0 && (
            <span className="text-xs text-on-surface-dim">
              {tags.join('  ·  ')}
            </span>
          )}
        </div>
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="px-3 text-bad hover:bg-bad/10 transition-colors flex items-center"
        aria-label="Excluir"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

function flagsTags(s: CatServico): string {
  const f: string[] = []
  if (s.flagQtdExecutada) f.push('qtd')
  if (s.flagAlturaMedia) f.push('h')
  if (s.flagDn) f.push('dn')
  if (s.flagMaterialRede) f.push('mat')
  if (s.flagTpRede) f.push('tp')
  return f.length === 0 ? '—' : f.join(' ')
}

function formatNumber(n: number): string {
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 3 })
}

function formatBR(isoDate: string): string {
  const [y, m, d] = isoDate.split('-')
  return `${d}/${m}/${y}`
}
